import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchOutlined, SettingOutlined } from '@ant-design/icons';
import { SearchInput } from '@/shared/ui/SearchInput';
import { MIN_QUERY_LENGTH } from '@/utils/search';
import { useSearchQuery } from '@/state/slices/search';
import { useDebounce } from '@/hooks/useDebounce';
import { useFlag, type FlagKey } from '@/hooks/useFlag';
import { Highlight } from '@/shared/ui/Highlight';
import styles from './GlobalSearch.module.css';
import { DropdownContent } from './DropdownContent';

const isMac =
  typeof navigator !== 'undefined' &&
  /mac|iphone|ipad/i.test(navigator.userAgent);

const DEBOUNCE_MS = 300;
const DROPDOWN_LIMIT = 5;

interface SettingsCommand {
  label: string;
  section: string;
  keywords: string;
  /** Hidden while this flag is off. */
  flag?: FlagKey;
}

/** "Remember this device" → "remember-this-device" */
function toAnchorId(label: string): string {
  return label.toLowerCase().replace(/\s+/g, '-');
}

const SETTINGS_COMMANDS: SettingsCommand[] = [
  { label: 'Autoplay', section: 'viewer', keywords: 'autoplay video play' },
  {
    label: 'Autosave',
    section: 'reporting',
    keywords: 'auto save autosave worksheet',
    flag: 'autosave',
  },
  {
    label: 'Change password',
    section: 'security',
    keywords: 'password change',
  },
  { label: 'Cine speed', section: 'viewer', keywords: 'cine speed playback' },
  {
    label: 'Collapsed assignments',
    section: 'viewer',
    keywords: 'assignments panel collapse sidebar',
  },
  {
    label: 'Collapsed context section',
    section: 'viewer',
    keywords: 'context section collapse',
  },
  {
    label: 'Collapsed measurement section',
    section: 'viewer',
    keywords: 'measurement section collapse',
  },
  {
    label: 'Compact mode',
    section: 'appearance',
    keywords: 'compact relaxed density spacing',
  },
  {
    label: 'Credentials line',
    section: 'reporting',
    keywords: 'credentials signature md do facc',
  },
  { label: 'Dark mode', section: 'appearance', keywords: 'dark light theme' },
  {
    label: 'Default reader worksheet',
    section: 'reporting',
    keywords: 'worksheet reader default reading',
  },
  {
    label: 'Default reporting template',
    section: 'reporting',
    keywords: 'template report default',
  },
  {
    label: 'Font size',
    section: 'appearance',
    keywords: 'font size text small medium large typography',
  },
  {
    label: 'Imperial units',
    section: 'reporting',
    keywords: 'imperial metric units height weight inches lbs cm kg',
  },
  {
    label: 'Inactivity timeout',
    section: 'security',
    keywords: 'inactivity timeout idle auto logout',
  },
  {
    label: 'Panel layout',
    section: 'viewer',
    keywords: 'panel layout grid viewport arrangement',
  },
  {
    label: 'Popped out viewer',
    section: 'viewer',
    keywords: 'pop out separate window viewer detach',
  },
  {
    label: 'Remember this device',
    section: 'security',
    keywords: 'remember device trust browser',
  },
  {
    label: 'Session duration',
    section: 'security',
    keywords: 'session duration login expiry',
  },
  {
    label: 'Two-Factor Authentication',
    section: 'security',
    keywords: 'mfa 2fa two factor authentication',
  },
  {
    label: 'Warn unsaved changes',
    section: 'reporting',
    keywords: 'warn unsaved changes navigation leave',
  },
];

const SECTION_LABELS: Record<string, string> = {
  appearance: 'Appearance',
  viewer: 'Viewer',
  reporting: 'Reporting',
  security: 'Account & Security',
};

const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debouncedQuery = useDebounce(query, DEBOUNCE_MS);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [highlightIndex, setHighlightIndex] = useState(-1);

  const isCommandMode = query.startsWith('>');
  const commandQuery = isCommandMode ? query.slice(1).trim().toLowerCase() : '';

  const { data, isFetching } = useSearchQuery(debouncedQuery, {
    skip: isCommandMode || debouncedQuery.length < MIN_QUERY_LENGTH,
  });

  const autosaveFlag = useFlag('autosave');

  const filteredCommands = useMemo(() => {
    if (!isCommandMode) return [];
    // The Autosave setting is hidden while the `autosave` flag is off.
    const commands = autosaveFlag
      ? SETTINGS_COMMANDS
      : SETTINGS_COMMANDS.filter((cmd) => cmd.flag !== 'autosave');
    if (!commandQuery) return commands;
    return commands.filter((cmd) => {
      const haystack = `${cmd.label} ${cmd.keywords}`.toLowerCase();
      return haystack.includes(commandQuery);
    });
  }, [isCommandMode, commandQuery, autosaveFlag]);

  // Reset highlight when results change
  useEffect(() => {
    setHighlightIndex(-1);
  }, [commandQuery, debouncedQuery]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Show dropdown when debounced query meets minimum length or in command mode
  useEffect(() => {
    if (isCommandMode) {
      setIsOpen(true);
    } else if (debouncedQuery.length >= MIN_QUERY_LENGTH) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [debouncedQuery, isCommandMode]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setQuery(value);
      if (!value) {
        setIsOpen(false);
      }
    },
    []
  );

  const handleSelect = useCallback(
    (path: string) => {
      setIsOpen(false);
      setQuery('');
      navigate(path);
    },
    [navigate]
  );

  const handleCommandSelect = useCallback(
    (anchor: string) => {
      setIsOpen(false);
      setQuery('');
      navigate(`/settings#${anchor}`);
    },
    [navigate]
  );

  const handleSearchMode = useCallback(() => {
    setQuery('');
    setIsOpen(false);
    setHighlightIndex(-1);
  }, []);

  const handleCommandMode = useCallback(() => {
    setQuery('> ');
    setIsOpen(true);
  }, []);

  const handleShowAll = useCallback(() => {
    setIsOpen(false);
    const q = debouncedQuery || query;
    setQuery('');
    navigate(`/search?q=${encodeURIComponent(q)}`);
  }, [debouncedQuery, query, navigate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setHighlightIndex(-1);
        inputRef.current?.blur();
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((prev) => {
          const max = isCommandMode
            ? filteredCommands.length - 1
            : (data
                ? Math.min(data.patients.length, DROPDOWN_LIMIT) +
                  Math.min(data.studies.length, DROPDOWN_LIMIT) +
                  Math.min(data.reports.length, DROPDOWN_LIMIT)
                : 0) - 1;
          return prev < max ? prev + 1 : 0;
        });
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((prev) => {
          const max = isCommandMode
            ? filteredCommands.length - 1
            : (data
                ? Math.min(data.patients.length, DROPDOWN_LIMIT) +
                  Math.min(data.studies.length, DROPDOWN_LIMIT) +
                  Math.min(data.reports.length, DROPDOWN_LIMIT)
                : 0) - 1;
          return prev > 0 ? prev - 1 : max;
        });
        return;
      }

      if (e.key === 'Enter') {
        if (
          isCommandMode &&
          highlightIndex >= 0 &&
          filteredCommands[highlightIndex]
        ) {
          handleCommandSelect(
            toAnchorId(filteredCommands[highlightIndex].label)
          );
          return;
        }
        if (!isCommandMode && highlightIndex >= 0 && data) {
          // Build flat list capped to DROPDOWN_LIMIT per section
          const allItems = [
            ...data.patients
              .slice(0, DROPDOWN_LIMIT)
              .map((p) => `/patients/${p.uuid}`),
            ...data.studies
              .slice(0, DROPDOWN_LIMIT)
              .map((s) => `/studies/${s.uuid}`),
            ...data.reports
              .slice(0, DROPDOWN_LIMIT)
              .map((r) => `/reports/${r.uuid}/preview`),
          ];
          if (allItems[highlightIndex]) {
            handleSelect(allItems[highlightIndex]);
            return;
          }
        }
        if (!isCommandMode && debouncedQuery.length >= MIN_QUERY_LENGTH) {
          handleShowAll();
        }
      }
    },
    [
      isCommandMode,
      filteredCommands,
      data,
      highlightIndex,
      debouncedQuery,
      handleShowAll,
      handleCommandSelect,
      handleSelect,
    ]
  );

  const hasResults =
    data &&
    (data.patients.length > 0 ||
      data.studies.length > 0 ||
      data.reports.length > 0);

  const totalResults = data
    ? data.patients.length + data.studies.length + data.reports.length
    : 0;

  const showDropdown = isCommandMode
    ? isOpen
    : isOpen && debouncedQuery.length >= MIN_QUERY_LENGTH;

  return (
    <div className={styles.container} ref={containerRef}>
      <SearchInput
        ref={inputRef}
        icon={<SearchOutlined />}
        iconPosition="left"
        shortcutKeys={[isMac ? '⌘' : 'Ctrl', 'K', '/', 'P']}
        placeholder="Search patients, studies..."
        value={query}
        onChange={handleInputChange}
        onSearchMode={handleSearchMode}
        onCommandMode={handleCommandMode}
        onFocus={() => {
          if (isCommandMode) {
            setIsOpen(true);
          } else if (debouncedQuery.length >= MIN_QUERY_LENGTH) {
            setIsOpen(true);
          }
        }}
        onKeyDown={handleKeyDown}
      />

      {showDropdown && (
        <div className={styles.dropdown} role="listbox">
          {isCommandMode ? (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <SettingOutlined className={styles.sectionIcon} />
                Settings
              </div>
              {filteredCommands.length === 0 && (
                <div className={styles.empty}>No matching settings</div>
              )}
              {filteredCommands.map((cmd, idx) => (
                <div
                  key={cmd.label}
                  className={`${styles.resultItem} ${idx === highlightIndex ? styles.resultItemActive : ''}`}
                  role="option"
                  aria-selected={idx === highlightIndex}
                  tabIndex={-1}
                  onClick={() => handleCommandSelect(toAnchorId(cmd.label))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter')
                      handleCommandSelect(toAnchorId(cmd.label));
                  }}
                >
                  <div className={`${styles.resultIcon} ${styles.settingIcon}`}>
                    <SettingOutlined />
                  </div>
                  <div className={styles.resultContent}>
                    <span className={styles.resultPrimary}>
                      <Highlight text={cmd.label} query={commandQuery} />
                    </span>
                    <span className={styles.resultSecondary}>
                      {SECTION_LABELS[cmd.section] ?? cmd.section}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <DropdownContent
              isFetching={isFetching}
              hasResults={!!hasResults}
              data={data}
              debouncedQuery={debouncedQuery}
              handleSelect={handleSelect}
              totalResults={totalResults}
              handleShowAll={handleShowAll}
              highlightIndex={highlightIndex}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
